import { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import SiteFooter from './components/layout/SiteFooter';
import SiteHeader from './components/layout/SiteHeader';
import { useLocale } from './context/LocaleContext';
import { usePageEffects } from './hooks/usePageEffects';
import AboutPage from './pages/AboutPage';
import CompaniesPage from './pages/CompaniesPage';
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';
import PricingPage from './pages/PricingPage';
import ResourcePage from './pages/ResourcePage';
import SchoolsPage from './pages/SchoolsPage';

const titleKeyByPath = {
  '/': 'home',
  '/schulen': 'schools',
  '/unternehmen': 'companies',
  '/preise': 'pricing',
  '/ueber-uns': 'about',
  '/coach': 'coach',
  '/motivationsschreiben-lehrstelle': 'motivationLetter',
  '/lebenslauf-lehrstelle': 'cv',
  '/berufscheck': 'careerCheck',
  '/lehrstellen': 'apprenticeships',
};

export default function App() {
  const location = useLocation();
  const { t } = useLocale();
  usePageEffects(location.pathname);

  useEffect(() => {
    const pageKey = titleKeyByPath[location.pathname];
    const pageTitle = pageKey ? t.document.pageTitles[pageKey] : '404';
    const descriptionByPath = {
      '/': t.home.hero.subtitle,
      '/schulen': t.schools.hero.subtitle,
      '/unternehmen': t.companies.hero.subtitle,
      '/preise': t.pricing.hero.title,
      '/ueber-uns': t.about.hero.subtitle,
      '/coach': t.resources.coach.hero.subtitle,
      '/motivationsschreiben-lehrstelle': t.resources.motivationLetter.hero.subtitle,
      '/lebenslauf-lehrstelle': t.resources.cv.hero.subtitle,
      '/berufscheck': t.resources.careerCheck.hero.subtitle,
      '/lehrstellen': t.resources.apprenticeships.hero.subtitle,
    };
    const description = descriptionByPath[location.pathname] ?? t.home.hero.subtitle;
    const fullTitle = `${pageTitle} — ${t.document.titleSuffix}`;
    const canonicalUrl = `https://yourapplicant.ch${location.pathname === '/' ? '/' : location.pathname}`;

    document.title = fullTitle;
    document.querySelector('meta[name="description"]')?.setAttribute('content', description);
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', fullTitle);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', description);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', canonicalUrl);
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonicalUrl);
  }, [location.pathname, t]);

  return (
    <>
      <a className="skip-link" href="#main-content">{t.common.aria.skipToContent}</a>
      <SiteHeader />
      <Routes location={location}>
        <Route path="/" element={<HomePage />} />
        <Route path="/schulen" element={<SchoolsPage />} />
        <Route path="/unternehmen" element={<CompaniesPage />} />
        <Route path="/preise" element={<PricingPage />} />
        <Route path="/ueber-uns" element={<AboutPage />} />
        <Route path="/coach" element={<ResourcePage resourceKey="coach" />} />
        <Route path="/motivationsschreiben-lehrstelle" element={<ResourcePage resourceKey="motivationLetter" />} />
        <Route path="/lebenslauf-lehrstelle" element={<ResourcePage resourceKey="cv" />} />
        <Route path="/berufscheck" element={<ResourcePage resourceKey="careerCheck" />} />
        <Route path="/lehrstellen" element={<ResourcePage resourceKey="apprenticeships" />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <SiteFooter />
    </>
  );
}
